/*
  Animated steam over images (see snippets/steam-effect.liquid).

  Draws thin, curling wisps on a canvas laid over the image, like real
  steam off a hot drink. Each "x,y,size" point (in % of the original
  image) is mapped onto the image as it is actually drawn (object-fit
  cover / contain / fill and object-position), so the steam stays on the
  cups when the image is cropped differently at different screen sizes.
*/
(function () {
  if (window.__steamEffectLoaded) return;
  window.__steamEffectLoaded = true;

  var STRANDS = 3; // wisps per cup

  function parsePoints(str) {
    return (str || '')
      .split(';')
      .map(function (p) {
        var n = p.split(',').map(function (v) { return parseFloat(v); });
        // optional 4th value: lean (sideways drift per unit of height,
        // negative = left), so steam can rise clear of busy artwork
        return { x: n[0], y: n[1], size: n[2] || 15, lean: n[3] || 0 };
      })
      .filter(function (p) { return !isNaN(p.x) && !isNaN(p.y); });
  }

  // "50% 100%" / "center bottom" / "20px" -> fraction of the free space
  function positionPart(value, free) {
    if (!value) return 0.5;
    if (value === 'left' || value === 'top') return 0;
    if (value === 'right' || value === 'bottom') return 1;
    if (value === 'center') return 0.5;
    if (value.indexOf('%') > -1) return parseFloat(value) / 100;
    if (value.indexOf('px') > -1) return free ? parseFloat(value) / free : 0;
    return 0.5;
  }

  // Where the image is drawn inside its box
  // Inline SVG (e.g. an animated illustration): its box relative to the
  // layer's parent, and the viewBox drawn "contain" style inside it
  function svgBox(svg, parent) {
    var r = svg.getBoundingClientRect();
    var pr = parent.getBoundingClientRect();
    var vb = svg.viewBox && svg.viewBox.baseVal;
    if (!r.width || !r.height || !vb || !vb.width) return null;
    var s = Math.min(r.width / vb.width, r.height / vb.height);
    var dw = vb.width * s;
    var dh = vb.height * s;
    // Where the drawing sits in its box (preserveAspectRatio: xMidYMax etc.)
    var fx = 0.5, fy = 0.5;
    var par = svg.preserveAspectRatio && svg.preserveAspectRatio.baseVal;
    if (par && par.align >= 2 && par.align <= 10) {
      var idx = par.align - 2;
      fx = (idx % 3) / 2;
      fy = Math.floor(idx / 3) / 2;
    }
    return {
      x: (r.width - dw) * fx,
      y: (r.height - dh) * fy,
      w: dw,
      h: dh,
      boxLeft: r.left - pr.left,
      boxTop: r.top - pr.top,
      boxWidth: r.width,
      boxHeight: r.height
    };
  }

  function drawnBox(img) {
    var bw = img.clientWidth;
    var bh = img.clientHeight;
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!bw || !bh || !nw || !nh) return null;

    var style = getComputedStyle(img);
    var fit = style.objectFit || 'fill';
    var sx = bw / nw;
    var sy = bh / nh;
    if (fit === 'cover') sx = sy = Math.max(sx, sy);
    else if (fit === 'contain') sx = sy = Math.min(sx, sy);
    else if (fit === 'none') sx = sy = 1;
    else if (fit === 'scale-down') sx = sy = Math.min(1, Math.min(sx, sy));

    var dw = nw * sx;
    var dh = nh * sy;
    var pos = (style.objectPosition || '50% 50%').split(' ');
    return {
      x: (bw - dw) * positionPart(pos[0], bw - dw),
      y: (bh - dh) * positionPart(pos[1] || pos[0], bh - dh),
      w: dw,
      h: dh,
      boxLeft: img.offsetLeft,
      boxTop: img.offsetTop,
      boxWidth: bw,
      boxHeight: bh
    };
  }

  // Soft white dot, drawn once and stamped many times
  var sprite = (function () {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var g = c.getContext('2d');
    var grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.45)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    return c;
  })();

  // ---------------------------------------------------------------------
  // Realistic steam (WebGL): the technique used for "coffee smoke" shaders.
  // Each cup gets a tall quad filled with layered noise (fBm) that scrolls
  // upward; the column twists more the higher it goes, only the dense parts
  // of the noise are kept (wispy holes) and the edges fade out.
  // ---------------------------------------------------------------------
  var VERT = 'attribute vec2 aPos;attribute vec2 aUv;varying vec2 vUv;void main(){vUv=aUv;gl_Position=vec4(aPos,0.,1.);}';
  var FRAG = [
    'precision mediump float;',
    'varying vec2 vUv;',
    'uniform float uTime;uniform float uSeed;uniform float uAlpha;uniform float uLean;',
    'float hash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
    'float noise(vec2 p){vec2 i=floor(p);vec2 f=fract(p);f=f*f*(3.-2.*f);',
    ' return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x),f.y);}',
    'float fbm(vec2 p){float v=0.;float a=.5;for(int i=0;i<5;i++){v+=a*noise(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}',
    'void main(){',
    ' vec2 uv=vUv;',
    // twist / sway: grows with height, so the base stays on the cup
    ' float tw=(fbm(vec2(uv.y*1.6-uTime*.12,uSeed))-.5)*.75*uv.y;',
    ' uv.x+=tw+uLean*uv.y;',
    // stretched noise scrolling upward
    ' vec2 s=vec2(uv.x*1.4,uv.y*.9-uTime*.11)+uSeed;',
    ' float n=fbm(s*3.);',
    ' n=fbm(s*3.+vec2(n*1.3,0.));',              // warp it into itself: curly wisps
    // soft body + thin silky filaments (ridged noise = lines where the
    // noise crosses its middle), the filaments are what reads as steam
    ' float body=smoothstep(.5,.95,n);',
    ' float ridge=1.-abs(fbm(s*2.2+vec2(0.,n*.8))*2.-1.);',
    ' float fil=pow(ridge,5.);',
    ' float smoke=body*.5+fil*.62*smoothstep(.05,.35,uv.y);',
    ' smoke=min(smoke,.8);',
    // column: narrow at the cup, wider higher up
    ' float c=abs(uv.x-.5)*2.;float w=mix(.28,.95,smoothstep(0.,1.,uv.y));',
    ' smoke*=1.-smoothstep(w*.45,w,c);',
    // fade in just above the cup, fade out at the top
    ' smoke*=smoothstep(0.,.14,uv.y)*(1.-smoothstep(.45,1.,uv.y));',
    ' float a=smoke*uAlpha;',
    ' gl_FragColor=vec4(a,a,a,a);',                // premultiplied white
    '}'
  ].join('');

  function makeGL(canvas) {
    var gl;
    try {
      gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false });
    } catch (e) {}
    if (!gl) return null;
    function sh(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }
    var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    var aPos = gl.getAttribLocation(prog, 'aPos'), aUv = gl.getAttribLocation(prog, 'aUv');
    gl.enableVertexAttribArray(aPos);
    gl.enableVertexAttribArray(aUv);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    return {
      gl: gl,
      uTime: gl.getUniformLocation(prog, 'uTime'),
      uSeed: gl.getUniformLocation(prog, 'uSeed'),
      uAlpha: gl.getUniformLocation(prog, 'uAlpha'),
      uLean: gl.getUniformLocation(prog, 'uLean')
    };
  }

  function Steam(layer) {
    this.layer = layer;
    this.img = layer.parentElement.querySelector(layer.dataset.steamTarget || 'img');
    this.canvas = document.createElement('canvas');
    this.glr = makeGL(this.canvas);
    this.ctx = this.glr ? null : this.canvas.getContext('2d');
    // Over photos the steam brightens like real steam in light
    if (this.glr && layer.dataset.steamBlend !== 'soft') layer.style.mixBlendMode = 'screen';
    // A light blur keeps the wisps soft, never sharp white lines
    if (this.glr) this.canvas.style.filter = 'blur(0.8px)';
    layer.appendChild(this.canvas);
    this.points = parsePoints(layer.dataset.steamPoints);
    this.soft = layer.dataset.steamBlend === 'soft';
    this.strength = parseFloat(getComputedStyle(layer).getPropertyValue('--steam-opacity')) || 0.6;
    this.particles = [];
    this.emitters = [];
    this.visible = false;
    this.last = 0;
    this.ox = 0;
    this.oy = 0;
    this.tick = this.tick.bind(this);
  }

  Steam.prototype.layout = function () {
    // Look the target up each time: an inline SVG may be added later
    this.img = this.layer.parentElement.querySelector(this.layer.dataset.steamTarget || 'img');
    var box = null;
    if (this.img && this.img.getClientRects().length) {
      box = this.img instanceof SVGSVGElement ? svgBox(this.img, this.layer.parentElement) : drawnBox(this.img);
    }
    if (!box) {
      this.layer.style.display = 'none';
      this.emitters = [];
      return;
    }
    this.layer.style.display = '';
    this.layer.style.left = box.boxLeft + 'px';
    this.layer.style.top = box.boxTop + 'px';
    this.layer.style.width = box.boxWidth + 'px';
    this.layer.style.height = box.boxHeight + 'px';

    // One emitter per cup; sizes scale with how big the cup is on screen
    var old = this.emitters;
    this.emitters = this.points.map(function (p, i) {
      var w = (box.w * p.size) / 100;
      var prev = old[i];
      return {
        seed: prev ? prev.seed : Math.random() * 50,
        lean: p.lean,
        x: box.x + (box.w * p.x) / 100,
        y: box.y + (box.h * p.y) / 100,
        w: w,
        rise: w * 2.2, // how high the steam climbs before it fades
        strands: prev ? prev.strands : Array.apply(null, Array(STRANDS)).map(function (_, s) {
          return {
            offset: (s - (STRANDS - 1) / 2) * 0.22 + (Math.random() - 0.5) * 0.08,
            phase: Math.random() * Math.PI * 2,
            freq: 1.6 + Math.random() * 1.2,
            speed: 0.7 + Math.random() * 0.5,
            carry: 0
          };
        })
      };
    });

    // The canvas only covers the area the steam can reach (not the whole
    // image) and draws at 1x - steam is soft, so it needs no retina detail
    if (!this.emitters.length) return;
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    this.emitters.forEach(function (e) {
      var drift = e.lean * e.rise * 1.25;
      x0 = Math.min(x0, e.x - e.w * 1.6 + Math.min(0, drift));
      x1 = Math.max(x1, e.x + e.w * 1.6 + Math.max(0, drift));
      y0 = Math.min(y0, e.y - e.rise * 1.35 - e.w * 0.4);
      y1 = Math.max(y1, e.y + e.w * 0.2);
    });
    x0 = Math.max(0, Math.floor(x0));
    y0 = Math.max(0, Math.floor(y0));
    x1 = Math.min(box.boxWidth, Math.ceil(x1));
    y1 = Math.min(box.boxHeight, Math.ceil(y1));
    this.ox = x0;
    this.oy = y0;
    this.canvas.width = Math.max(1, x1 - x0);
    this.canvas.height = Math.max(1, y1 - y0);
    this.canvas.style.marginLeft = x0 + 'px';
    this.canvas.style.marginTop = y0 + 'px';
    if (this.glr) this.glr.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  };

  // WebGL frame: one noise quad per cup
  Steam.prototype.drawGL = function (t) {
    var r = this.glr, gl = r.gl, cw = this.canvas.width, ch = this.canvas.height, self = this;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(r.uTime, t);
    this.emitters.forEach(function (e) {
      var x = e.x - self.ox, y = e.y - self.oy;
      var hw = e.w * 1.15, top = y - e.rise * 1.3, bottom = y + e.w * 0.05;
      var lx = (x - hw) / cw * 2 - 1, rx = (x + hw) / cw * 2 - 1;
      var ty = 1 - top / ch * 2, by = 1 - bottom / ch * 2;
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        lx, by, 0, 0,  rx, by, 1, 0,  lx, ty, 0, 1,  rx, ty, 1, 1
      ]), gl.DYNAMIC_DRAW);
      gl.uniform1f(r.uSeed, e.seed);
      gl.uniform1f(r.uLean, e.lean * 1.4);
      gl.uniform1f(r.uAlpha, Math.min(0.85, self.strength * (self.soft ? 0.6 : 0.75)));
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    });
  };

  // Smooth 1D value noise (0..1), so the drift is irregular, not a sine
  function noise(x) {
    var i = Math.floor(x), f = x - i;
    var a = Math.sin(i * 127.1) * 43758.5453, b = Math.sin((i + 1) * 127.1) * 43758.5453;
    a -= Math.floor(a);
    b -= Math.floor(b);
    f = f * f * (3 - 2 * f);
    return a + (b - a) * f;
  }

  Steam.prototype.tick = function (now) {
    if (!this.visible) return;
    // ~30 frames a second is plenty for slow-moving steam
    if (this.last && now - this.last < 30) {
      requestAnimationFrame(this.tick);
      return;
    }
    var dt = Math.min(0.08, (now - (this.last || now)) / 1000);
    this.last = now;
    var t = now / 1000;
    if (this.glr) {
      this.drawGL(t % 1000);
      requestAnimationFrame(this.tick);
      return;
    }
    var ctx = this.ctx;
    var self = this;
    var ox = this.ox, oy = this.oy;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // "soft" = normal blending, reads better over light illustrations;
    // default "lighter" glows nicely over dark photos
    ctx.globalCompositeOperation = this.soft ? 'source-over' : 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    this.emitters.forEach(function (e) {
      // Faint haze for volume: a few big, very soft puffs
      e.haze = e.haze || [];
      e.hazeCarry = (e.hazeCarry || 0) + dt * 1.6;
      while (e.hazeCarry >= 1) {
        e.hazeCarry -= 1;
        e.haze.push({ age: 0, life: 3 + Math.random() * 1.5, dx: (Math.random() - 0.5) * 0.5 });
      }
      e.haze = e.haze.filter(function (h) {
        h.age += dt;
        var k = h.age / h.life;
        if (k >= 1) return false;
        var r = e.w * (0.35 + 0.6 * k);
        var x = e.x - ox + e.w * h.dx * k + e.lean * k * e.rise;
        var y = e.y - oy - k * e.rise * 0.8;
        ctx.globalAlpha = self.strength * 0.07 * Math.sin(k * Math.PI);
        ctx.drawImage(sprite, x - r, y - r, r * 2, r * 2);
        return true;
      });

      e.strands.forEach(function (s) {
        s.pts = s.pts || [];
        // Puffs: emit for a while, pause briefly, so ribbons break into wisps
        s.timer = (s.timer === undefined ? Math.random() * 1.5 : s.timer) - dt;
        if (s.timer <= 0) {
          s.on = !s.on;
          s.timer = s.on ? 1.1 + Math.random() * 1.6 : 0.25 + Math.random() * 0.6;
          if (s.on) s.puff = (s.puff || 0) + 1;
        }
        s.carry += dt * 24;
        while (s.carry >= 1) {
          s.carry -= 1;
          if (!s.on) continue;
          s.pts.push({
            puff: s.puff,
            age: 0,
            life: 2.8 + Math.random() * 0.6,
            x: e.w * s.offset * 0.6,
            vx: 0,
            y: 0
          });
        }

        // Move: rise, slow down near the top; drift on an irregular flow that
        // is shared by the whole strand (so it stays one ribbon) plus a little
        // random walk per point (so the ribbon feathers and spreads)
        var alive = [];
        for (var i = 0; i < s.pts.length; i++) {
          var p = s.pts[i];
          p.age += dt;
          var k = p.age / p.life;
          if (k >= 1) continue;
          var h = -p.y;
          var flow = (noise(h / (e.w * 0.9) + s.phase * 3 - t * 0.6) - 0.5) * 2 +
            Math.sin(h / (e.w * 0.55) * s.freq + s.phase - t * 1.1) * 0.6;
          p.vx += (Math.random() - 0.5) * e.w * 0.9 * dt;
          p.vx *= 0.96;
          p.x += (flow * e.w * (0.18 + 1.1 * k) * 0.9 + e.lean * e.w * 2.2) * dt + p.vx * dt;
          p.y -= e.rise * s.speed * (1.25 - 0.55 * k) / p.life * dt * 1.15;
          alive.push(p);
        }
        s.pts = alive;

        // Draw each puff as a ribbon: wide faint glow + thin brighter core,
        // getting wider and fainter as it rises
        for (var pass = 0; pass < 2; pass++) {
          for (var j = 1; j < s.pts.length; j++) {
            var a0 = s.pts[j - 1], a1 = s.pts[j];
            if (a0.puff !== a1.puff) continue;
            var kk = a1.age / a1.life;
            var fade = Math.min(1, kk * 8) * Math.pow(1 - kk, 1.4);
            var x0 = e.x - ox + a0.x, y0 = e.y - oy + a0.y;
            var x1 = e.x - ox + a1.x, y1 = e.y - oy + a1.y;
            if (pass === 0) {
              ctx.lineWidth = e.w * (0.09 + 0.32 * kk);
              ctx.globalAlpha = self.strength * 0.09 * fade;
            } else {
              ctx.lineWidth = Math.max(1, e.w * (0.025 + 0.07 * kk));
              ctx.globalAlpha = self.strength * 0.38 * fade * (1 - kk * 0.5);
            }
            ctx.strokeStyle = '#fff';
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.stroke();
          }
        }
      });
    });

    ctx.globalAlpha = 1;
    requestAnimationFrame(this.tick);
  };

  Steam.prototype.setVisible = function (v) {
    if (v === this.visible) return;
    this.visible = v;
    if (v) {
      this.last = 0;
      requestAnimationFrame(this.tick);
    }
  };

  function init(layer) {
    if (layer.__steam || !layer.parentElement) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var steam = (layer.__steam = new Steam(layer));
    var run = function () { steam.layout(); };
    if (steam.img && !steam.img.complete) steam.img.addEventListener('load', run, { once: true });
    run();

    if ('ResizeObserver' in window) new ResizeObserver(run).observe(layer.parentElement);
    else window.addEventListener('resize', run);

    // Only animate while the image is on screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { steam.setVisible(en.isIntersecting); });
      }, { rootMargin: '100px' }).observe(layer.parentElement);
    } else {
      steam.setVisible(true);
    }
  }

  function initAll() {
    document.querySelectorAll('.steam-layer').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Theme editor: sections re-render without a page load
  document.addEventListener('shopify:section:load', initAll);

  // Other scripts (e.g. the Daisy hero) ask for a re-measure after they
  // add or move the target
  document.addEventListener('steam:relayout', function () {
    initAll();
    document.querySelectorAll('.steam-layer').forEach(function (l) {
      if (l.__steam) l.__steam.layout();
    });
  });
})();
